<template>
  <RevButton
    v-if="isVisible"
    :disabled="isDisabled"
    full-width="adaptive"
    :loading="isLoading"
    size="small"
    variant="secondary"
    @click="handleButtonClick"
  >
    {{ i18n(translations.label) }}
  </RevButton>
  <RevDialog :name="MODAL_NAME" :title="i18n(translations.label)">
    <template #body>
      <div class="my-24">{{ i18n(translations.modalMessage) }}</div>
    </template>

    <template #footer>
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          full-width="always"
          :loading="isLoading"
          variant="primary"
          @click="handleModalConfirm"
        >
          {{ i18n(translations.modalConfirm) }}
        </RevButton>

        <RevLink @click="handleModalCancel">
          {{ i18n(translations.modalCancel) }}
        </RevLink>
      </div>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { postMerchantOrdersExport } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import { RevLink } from '@ds/components/Link'
import { closeModal, openModal } from '@ds/components/ModalBase'

import type { Orders, Preset } from '../../../Tab.types'

import { MODAL_NAME } from './CreateBackshipLabels.constants'
import translations from './CreateBackshipLabels.translations'
import { getData } from './CreateBackshipLabels.utils'

const props = defineProps<{ preset: Preset; selectedOrders: Orders }>()
const emit = defineEmits(['success'])

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()

const isLoading = ref<boolean>(false)
const isDisabled = computed(() => props.selectedOrders.length === 0)
const isVisible = computed(() => ['all', 'pending'].includes(props.preset))
const data = computed(() => getData(props.selectedOrders))

async function callApi() {
  isLoading.value = true
  try {
    await $httpFetch(postMerchantOrdersExport, {
      pathParams: { exportType: 'backship-labels' },
      body: { orderIds: data.value.orderIdsToValidate },
    })
    openSuccessToast({ content: i18n(translations.toastSuccess) })
    emit('success')
  } catch (error) {
    const { title: content } = HttpApiError.fromAnyError(error as Error)
    openErrorToast({ content })
  } finally {
    isLoading.value = false
    closeModal(MODAL_NAME)
  }
}

function handleButtonClick() {
  if (data.value.ordersThatCanNotBeConfirmed.length > 0) {
    return openModal(MODAL_NAME)
  }

  return callApi()
}

function handleModalConfirm() {
  callApi()
}

function handleModalCancel() {
  closeModal(MODAL_NAME)
}
</script>
